import type { ApplicationConfig } from '@angular/core';
import { importProvidersFrom, inject, isDevMode, provideAppInitializer } from '@angular/core';
import { MAT_BOTTOM_SHEET_DATA } from '@angular/material/bottom-sheet';
import { MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS } from '@angular/material/button-toggle';
import { provideNativeDateAdapter } from '@angular/material/core';
import { Title } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withRouterConfig } from '@angular/router';
import { provideServiceWorker, SwUpdate } from '@angular/service-worker';
import { BecksNativeDateTimeModule } from '@becksdevteam/angular-common/datetime-picker';
import { provideEffects } from '@ngrx/effects';
import { provideRouterStore } from '@ngrx/router-store';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { Angulartics2Module } from 'angulartics2';
import { provideHighcharts } from 'highcharts-angular';
import { APP_ROUTES } from './app.routes';
import { CoreModule } from './core/core.module';
import { ApplicationUpdateService } from './core/services/application-update.service';
import { HIGHCHARTS_GLOBAL_OPTIONS } from './shared/highcharts-global-options';
import { windowProvider, windowToken } from './shared/window-token';
import { CustomRouteSerializer, effects, reducers } from './store';

export const appConfig: ApplicationConfig = {
  providers: [
    importProvidersFrom(CoreModule, Angulartics2Module.forRoot(), BecksNativeDateTimeModule),
    provideRouter(APP_ROUTES, withRouterConfig({ paramsInheritanceStrategy: 'emptyOnly' })),
    provideStore(reducers, {
      runtimeChecks: {
        strictStateImmutability: true,
        strictActionImmutability: true,
        strictActionWithinNgZone: true,
      },
    }),
    provideEffects(effects),
    provideStoreDevtools({
      maxAge: 25, // Retains last 25 states
      logOnly: !isDevMode(), // Restrict extension to log-only mode
      autoPause: true, // Pauses recording actions and state changes when the extension window is not open
      trace: false, //  If set to true, will include stack trace for every dispatched action, so you can see it in trace tab jumping directly to that part of code
      traceLimit: 75, // maximum stack trace frames to be stored (in case trace option was provided as true)
      connectInZone: true, // If set to true, the connection is established within the Angular zone
    }),
    provideRouterStore({
      serializer: CustomRouteSerializer,
    }),
    Title,
    { provide: MAT_BOTTOM_SHEET_DATA, useValue: {} },
    { provide: MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS, useValue: { hideSingleSelectionIndicator: true } },
    { provide: windowToken, useFactory: windowProvider },
    provideNativeDateAdapter(),
    provideAnimations(),
    provideHighcharts({ options: HIGHCHARTS_GLOBAL_OPTIONS }),
    provideAppInitializer(() => {
      const updateService = inject(ApplicationUpdateService);
      const swUpdate = inject(SwUpdate);

      if (swUpdate.isEnabled) {
        updateService.init();
      }
    }),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      // Register the service worker once the app is stable, register after 30 seconds no matter what
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
};
